import PropTypes from 'prop-types';
import { inc, propOr } from 'ramda';
import {
  isRequiredBoolean,
  isRequiredInput,
  isRequiredSelect,
  nameIsValid
} from '@@viewValidations';
import {
  ControlledGranteeTypeSelect,
  ControlledInput,
  ControlledRadioButton,
  ControlledSelect,
  ControlledSSInput
} from '@@viewCommonComponents/controlled';
import useGranteeForm from '../../hooks/useGranteeForm';
import { DashedTitle, Icon, InfoMessage } from '@fasstech/middle-design-system';
import GranteeBirthDate from '@@viewCommonComponents/GranteeBirthdate';
import { booleanOptions, civilityOptions } from '@@viewCommonComponents/forms/options';
import TwinRanksSelect from '@@viewCommonComponents/TwinRanksSelect';
import { FormLayout } from '@@viewCommonComponents/forms/Layouts.jsx';
import { granteeIsTNSIsDisplayed, granteeIsTNSLabel } from '../../helpers/granteeIsTNS.js';
import { isDisplayedGranteeSitPro } from '../../helpers/isDisplayedGranteeSitPro.js';

const GranteeForm = ({
  index,
  regimeObligatoire,
  setDisplayDeletionModal,
  setDeletingGrantee,
  isDisabledAddingChild,
  isDisabledAddingSpouse,
  professionalSituations,
  getMiddleText,
  getMiddleBoolean,
  getMiddleNumber,
  selectedTab
}) => {
  const {
    id,
    getFieldsName,
    form,
    granteeDisabledTypes,
    granteeIsChild,
    granteeIsSpouse,
    granteeType,
    granteeTypeIsEmpty
  } = useGranteeForm({
    index,
    isDisabledAddingChild,
    isDisabledAddingSpouse,
    getMiddleText
  });
  const effectDate = form.watch('offer.effectDate');

  return (
    <FormLayout className={`${selectedTab !== inc(index) ? 'hidden' : ''}`}>
      <DashedTitle>{getMiddleText('BENEFICIAIRE_LABEL_GENERAL_VIEW_SUBTITLE')}</DashedTitle>

      <ControlledGranteeTypeSelect
        name={getFieldsName('type')}
        form={form}
        rules={isRequiredInput(getMiddleBoolean('BENEFICIAIRE_TYPE_EST_OBLIGATOIRE'))}
        label={getMiddleText('BENEFICIAIRE_TYPE_LABEL')}
        getMiddleText={getMiddleText}
        disabledTypes={granteeDisabledTypes}
        data-testid="BENEFICIAIRE_TYPE"
        isDisplayed={
          getMiddleBoolean('BENEFICIAIRE_TYPE_EST_NECESSAIRE') &&
          getMiddleBoolean('BENEFICIAIRE_TYPE_EST_DEMANDE_DONNEES_CONTRAT') &&
          !granteeTypeIsEmpty
        }
      />

      {getMiddleBoolean('BENEFICIAIRE_TYPE_EST_NECESSAIRE') && (
        <>
          {granteeIsSpouse && getMiddleBoolean('BENEFICIAIRE_NOTE_SPOUSE_EST_AFFICHEE') && (
            <div className="mt-2 col-span-2">
              <InfoMessage>{getMiddleBoolean('BENEFICIAIRE_NOTE_SPOUSE_LABEL')}</InfoMessage>
            </div>
          )}
          {granteeIsChild && getMiddleBoolean('BENEFICIAIRE_NOTE_CHILD_EST_AFFICHEE') && (
            <div className="mt-2 col-span-2">
              <InfoMessage>{getMiddleBoolean('BENEFICIAIRE_NOTE_CHILD_LABEL')}</InfoMessage>
            </div>
          )}
        </>
      )}

      <ControlledRadioButton
        name={getFieldsName('civility')}
        rules={isRequiredSelect(
          civilityOptions,
          getMiddleBoolean('BENEFICIAIRE_CIVILITE_EST_OBLIGATOIRE')
        )}
        form={form}
        options={civilityOptions}
        data-testid="BENEFICIAIRE_CIVILITE"
        isDisplayed={
          getMiddleBoolean('BENEFICIAIRE_CIVILITE_EST_NECESSAIRE') &&
          getMiddleBoolean('BENEFICIAIRE_CIVILITE_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />

      <GranteeBirthDate
        granteeType={granteeType}
        granteeIsChild={granteeIsChild}
        getFieldsName={getFieldsName}
        form={form}
        getMiddleText={getMiddleText}
        getMiddleBoolean={getMiddleBoolean}
        getMiddleNumber={getMiddleNumber}
        data-testid="BENEFICIAIRE_DATE_NAISSANCE"
        isDisplayed={
          getMiddleBoolean('BENEFICIAIRE_DATE_NAISSANCE_EST_NECESSAIRE') &&
          getMiddleBoolean('BENEFICIAIRE_DATE_NAISSANCE_EST_DEMANDE_DONNEES_CONTRAT')
        }
        effectDate={effectDate}
      />

      <ControlledRadioButton
        label={granteeIsTNSLabel({ getMiddleText, granteeIsSpouse })}
        name={getFieldsName('isTNS')}
        options={booleanOptions}
        rules={isRequiredBoolean(getMiddleBoolean('BENEFICIAIRE_ID_TNS_EST_OBLIGATOIRE'))}
        form={form}
        data-testid="BENEFICIAIRE_IS_TNS"
        isDisplayed={granteeIsTNSIsDisplayed({ getMiddleBoolean, granteeIsSpouse })}
      />

      <ControlledInput
        name={getFieldsName('lastName')}
        label={getMiddleText('BENEFICIAIRE_NOM_LABEL')}
        form={form}
        rules={nameIsValid(getMiddleBoolean('BENEFICIAIRE_NOM_EST_OBLIGATOIRE'))}
        data-testid="BENEFICIAIRE_NOM"
        isDisplayed={
          getMiddleBoolean('BENEFICIAIRE_NOM_EST_NECESSAIRE') &&
          getMiddleBoolean('BENEFICIAIRE_NOM_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />

      <ControlledInput
        name={getFieldsName('firstName')}
        label={getMiddleText('BENEFICIAIRE_PRENOM_LABEL')}
        form={form}
        rules={nameIsValid(getMiddleBoolean('BENEFICIAIRE_PRENOM_EST_OBLIGATOIRE'))}
        data-testid="BENEFICIAIRE_PRENOM"
        isDisplayed={
          getMiddleBoolean('BENEFICIAIRE_PRENOM_EST_NECESSAIRE') &&
          getMiddleBoolean('BENEFICIAIRE_PRENOM_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />

      <ControlledSelect
        name={getFieldsName('CSPs.0.categoryLvl1CSPExternalId')}
        label={getMiddleText('BENEFICIAIRE_SIT_PRO_LABEL')}
        rules={isRequiredSelect(
          professionalSituations,
          getMiddleBoolean('BENEFICIAIRE_SIT_PRO_EST_OBLIGATOIRE')
        )}
        form={form}
        data-testid="BENEFICIAIRE_SIT_PRO"
        options={professionalSituations || []}
        isDisplayed={isDisplayedGranteeSitPro({ getMiddleBoolean, granteeIsChild })}
      />

      <ControlledSelect
        name={getFieldsName('socialSecurities.0.regime')}
        label={getMiddleText('BENEFICIAIRE_REGIME_LABEL')}
        rules={isRequiredSelect(
          propOr([], 'regimes', regimeObligatoire),
          getMiddleBoolean('BENEFICIAIRE_REGIME_EST_OBLIGATOIRE')
        )}
        form={form}
        options={propOr([], 'regimes', regimeObligatoire)}
        data-testid="BENEFICIAIRE_REGIME"
        isDisplayed={
          getMiddleBoolean('BENEFICIAIRE_REGIME_EST_NECESSAIRE') &&
          getMiddleBoolean('BENEFICIAIRE_REGIME_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />

      <ControlledSSInput
        SSInputName={getFieldsName('socialSecurities.0.number')}
        birthDateInputName={getFieldsName('birthDate')}
        label={getMiddleText('BENEFICIAIRE_NSS_LABEL')}
        form={form}
        mandatory={getMiddleBoolean('BENEFICIAIRE_NSS_EST_OBLIGATOIRE')}
        data-testid="BENEFICIAIRE_NSS"
        isDisplayed={
          getMiddleBoolean('BENEFICIAIRE_NSS_EST_NECESSAIRE') &&
          getMiddleBoolean('BENEFICIAIRE_NSS_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />

      <TwinRanksSelect
        name={getFieldsName('socialSecurities.0.twinsRank')}
        label={getMiddleText('BENEFICIAIRE_RANG_LABEL')}
        tooltip={getMiddleText('BENEFICIAIRE_RANG_INFO_BULLE')}
        form={form}
        rules={getMiddleBoolean('BENEFICIAIRE_RANG_EST_OBLIGATOIRE')}
        data-testid="BENEFICIAIRE_RANG"
        isDisplayed={
          getMiddleBoolean('BENEFICIAIRE_RANG_EST_NECESSAIRE') &&
          getMiddleBoolean('BENEFICIAIRE_RANG_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />

      <div className={'flex items-center cursor-pointer w-full md:w-[100%]!'}>
        <Icon className={'mr-2'} name="delete" size={20} />
        <div
          onClick={() => {
            setDeletingGrantee({
              id,
              index
            });
            setDisplayDeletionModal(true);
          }}
          data-testid="BENEFICIAIRE_SUPPRESSION">
          {getMiddleText('BENEFICIAIRE_LABEL_SUPPRESSION_BOUTON_LABEL')}
        </div>
      </div>
    </FormLayout>
  );
};

GranteeForm.propTypes = {
  index: PropTypes.number,
  regimeObligatoire: PropTypes.object,
  displayDeletionModal: PropTypes.bool,
  setDisplayDeletionModal: PropTypes.func,
  isDisabledAddingChild: PropTypes.func,
  isDisabledAddingSpouse: PropTypes.func,
  getMiddleText: PropTypes.func,
  getMiddleBoolean: PropTypes.func,
  getMiddleNumber: PropTypes.func,
  deletionMessage: PropTypes.string,
  selectedTab: PropTypes.number
};

export default GranteeForm;
